/**
 * @fileoverview AR-805 — handles the Google OAuth callback on the integrations
 * page. The backend flow (PR #1384) sends the manager to Google and Google
 * returns to this same page with `?code=` (or `?error=`); we exchange the code
 * and clear the query so a refresh never replays a used code.
 */

import { useEffect, useRef } from 'react'
import { useSearchParams } from 'react-router-dom'

interface UseOauthCallbackParams {
  /** Exchanges the code; returns false when the exchange failed. */
  readonly completeConnect: (code: string) => Promise<boolean>
  readonly onSuccess: () => void
  readonly onError: () => void
  /** The manager pressed Cancel on Google's screen — not a failure. */
  readonly onCancelled: () => void
}

export function useOauthCallback({
  completeConnect,
  onSuccess,
  onError,
  onCancelled,
}: UseOauthCallbackParams): void {
  const [searchParams, setSearchParams] = useSearchParams()
  // A code is single-use: guard against the effect running twice.
  const handled = useRef(false)

  useEffect(() => {
    const code = searchParams.get('code')
    const error = searchParams.get('error')
    if (!code && !error) return
    if (handled.current) return
    handled.current = true

    // Clear BEFORE exchanging: while the code sits in the URL any script on
    // the page (and the Referer header) can read it, and a refresh would
    // replay a single-use code. Google's own guidance is to strip the
    // response parameters as soon as the request is handled.
    setSearchParams({}, { replace: true })

    if (error || !code) {
      // `access_denied` is the manager choosing not to grant access.
      error === 'access_denied' ? onCancelled() : onError()
      return
    }

    completeConnect(code).then(ok => (ok ? onSuccess() : onError()))
  }, [searchParams, setSearchParams, completeConnect, onSuccess, onError, onCancelled])
}
